import { useMutation, useQuery } from "@apollo/client/react";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  ShopOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { Alert, App, Button, Descriptions, Flex, Form, Input, Space, Tabs, Typography } from "antd";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { BreadcrumbTrail } from "../../components/BreadcrumbTrail.tsx";
import { LoadingBlocker } from "../../components/LoadingBlocker.tsx";
import { NotFoundWithBack } from "../../components/NotFoundWithBack.tsx";
import { PageLoader } from "../../components/PageLoader.tsx";
import {
  DeleteProducerDocument,
  GetProducerByIdDocument,
  type ProducerFragment,
  RestoreProducerDocument,
  UpdateProducerByIdDocument,
} from "../../gql/generated/graphqlSchema.tsx";
import { useSafeParams } from "../../hooks/useSafeParams.ts";
import { useTrailLabel } from "../../hooks/useTrailLabel.ts";
import { routes } from "../../routes.ts";
import { formatDateTime } from "../../utils/datetime.ts";
import { banner, fitContent, formNarrow, title } from "../../styles/shared.styles.ts";
import { normalizeOrNull, normalizeString } from "../../utils/stringNormalize.ts";
import { ProducerTab } from "./constants.ts";
import { ProducerUsedInRecipesTab } from "./ProducerUsedInRecipesTab.tsx";

const paramsSchema = z.object({ id: z.string().min(1) });
const viewTabSchema = z.enum(ProducerTab).catch(ProducerTab.General);

type ProducerFormValues = {
  companyName: string;
  address: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
};

const ProducerDetailView = ({
  producer,
  refetch,
}: {
  readonly producer: ProducerFragment;
  readonly refetch: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { modal, message } = App.useApp();
  useTrailLabel(producer.companyName, ShopOutlined);

  const [deleteProducer, { loading: deleting }] = useMutation(DeleteProducerDocument);
  const [restoreProducer, { loading: restoring }] = useMutation(RestoreProducerDocument);

  const activeTab = viewTabSchema.parse(location.hash.replace("#", ""));

  const handleDelete = () => {
    modal.confirm({
      title: t("producers.deleteTitle"),
      content: t("producers.deleteConfirm"),
      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
      cancelButtonProps: { icon: <CloseOutlined /> },
      okText: t("producers.delete"),
      onOk: async () => {
        await deleteProducer({ variables: { id: producer.id } });
        void message.success(t("producers.deletedDone"));
        refetch();
      },
    });
  };

  const handleRestore = () => {
    modal.confirm({
      title: t("producers.restoreTitle"),
      content: t("producers.restoreConfirm"),
      okButtonProps: { icon: <UndoOutlined /> },
      cancelButtonProps: { icon: <CloseOutlined /> },
      okText: t("producers.restore"),
      onOk: async () => {
        await restoreProducer({ variables: { id: producer.id } });
        void message.success(t("producers.restoredDone"));
        refetch();
      },
    });
  };

  return (
    <Flex gap="middle" vertical>
      <Flex gap="small" vertical>
        <BreadcrumbTrail />
        <Typography.Title className={title} level={3}>
          {producer.companyName}
        </Typography.Title>
        {producer.deleted ? (
          <Alert
            action={
              <Button icon={<UndoOutlined />} loading={restoring} onClick={handleRestore}>
                {t("producers.restore")}
              </Button>
            }
            className={banner}
            showIcon
            title={t("producers.deletedBanner")}
            type="error"
          />
        ) : (
          <Space wrap>
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(routes.producerDetailEdit.to(producer.id))}
              type="primary"
            >
              {t("producers.edit")}
            </Button>
            <Button
              color="danger"
              icon={<DeleteOutlined />}
              loading={deleting}
              onClick={handleDelete}
              variant="outlined"
            >
              {t("producers.delete")}
            </Button>
          </Space>
        )}
      </Flex>

      <Tabs
        activeKey={activeTab}
        destroyOnHidden
        items={[
          {
            key: ProducerTab.General,
            label: t("producers.tabGeneral"),
            children: (
              <Descriptions bordered className={fitContent} column={1} size="small">
                <Descriptions.Item label={t("producers.companyName")}>{producer.companyName}</Descriptions.Item>
                <Descriptions.Item label={t("producers.address")}>{producer.address ?? "\u2014"}</Descriptions.Item>
                <Descriptions.Item label={t("producers.email")}>{producer.email ?? "\u2014"}</Descriptions.Item>
                <Descriptions.Item label={t("producers.phone")}>{producer.phone ?? "\u2014"}</Descriptions.Item>
                <Descriptions.Item label={t("producers.website")}>{producer.website ?? "\u2014"}</Descriptions.Item>
                <Descriptions.Item label={t("producers.createdAt")}>
                  {formatDateTime(producer.createdAt, i18n.language)}
                </Descriptions.Item>
                <Descriptions.Item label={t("common.createdBy")}>{producer.createdBy.name}</Descriptions.Item>
                <Descriptions.Item label={t("producers.updatedAt")}>
                  {formatDateTime(producer.updatedAt, i18n.language)}
                </Descriptions.Item>
              </Descriptions>
            ),
          },
          {
            key: ProducerTab.UsedInRecipes,
            label: t("producers.tabUsedInRecipes", { count: producer.usedInRecipes.length }),
            children: <ProducerUsedInRecipesTab producer={producer} />,
          },
        ]}
        onChange={(tab) => navigate({ hash: tab }, { replace: true })}
      />
    </Flex>
  );
};

// eslint-disable-next-line react/no-multi-comp
const ProducerDetailEdit = ({ producer }: { readonly producer: ProducerFragment }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = App.useApp();
  useTrailLabel(producer.companyName, ShopOutlined);
  const [form] = Form.useForm<ProducerFormValues>();

  const [updateProducer, { loading: saving }] = useMutation(UpdateProducerByIdDocument);

  const onSave = async (values: ProducerFormValues) => {
    const result = await updateProducer({
      variables: {
        id: producer.id,
        input: {
          companyName: normalizeString(values.companyName),
          address: normalizeOrNull(values.address),
          email: normalizeOrNull(values.email),
          phone: normalizeOrNull(values.phone),
          website: normalizeOrNull(values.website),
        },
      },
    });
    if (result.data?.updateProducer) {
      void message.success(t("producers.saved"));
      void navigate(routes.producerDetail.to(producer.id));
    }
  };

  return (
    <Flex gap="middle" vertical>
      <LoadingBlocker loading={saving} />
      <Flex gap="small" vertical>
        <BreadcrumbTrail />
        <Typography.Title className={title} level={3}>
          {producer.companyName}
        </Typography.Title>
      </Flex>

      <Form<ProducerFormValues>
        className={formNarrow}
        form={form}
        initialValues={{
          companyName: producer.companyName,
          address: producer.address ?? null,
          email: producer.email ?? null,
          phone: producer.phone ?? null,
          website: producer.website ?? null,
        }}
        layout="vertical"
        onFinish={onSave}
        requiredMark={false}
      >
        <Form.Item
          label={t("producers.companyName")}
          name="companyName"
          rules={[{ required: true, message: t("producers.validation.companyNameRequired") }]}
        >
          <Input />
        </Form.Item>

        <Form.Item label={t("producers.address")} name="address">
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
        </Form.Item>

        <Form.Item
          label={t("producers.email")}
          name="email"
          rules={[{ type: "email", message: t("producers.validation.emailInvalid") }]}
        >
          <Input />
        </Form.Item>

        <Form.Item label={t("producers.phone")} name="phone">
          <Input />
        </Form.Item>

        <Form.Item label={t("producers.website")} name="website">
          <Input />
        </Form.Item>

        <Form.Item>
          <Space wrap>
            <Button htmlType="submit" icon={<CheckOutlined />} loading={saving} type="primary">
              {t("producers.save")}
            </Button>
            <Button icon={<CloseOutlined />} onClick={() => navigate(routes.producerDetail.to(producer.id))}>
              {t("common.cancel")}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Flex>
  );
};

// eslint-disable-next-line react/no-multi-comp
const ProducerDetailPageInner = ({ mode }: { readonly mode: "view" | "edit" }) => {
  const { t } = useTranslation();
  const { id } = useSafeParams(paramsSchema);
  const { data, loading, refetch } = useQuery(GetProducerByIdDocument, { variables: { id } });

  if (loading) {
    return <PageLoader />;
  }

  const producer = data?.producerById;
  if (!producer) {
    return <NotFoundWithBack description={t("producers.notFound")} />;
  }

  if (mode === "edit" && producer.deleted) {
    return <ProducerDetailView producer={producer} refetch={() => void refetch()} />;
  }

  return mode === "edit" ? (
    <ProducerDetailEdit producer={producer} />
  ) : (
    <ProducerDetailView producer={producer} refetch={() => void refetch()} />
  );
};

// eslint-disable-next-line react/no-multi-comp
export const ProducerDetailPage = () => <ProducerDetailPageInner mode="view" />;

// eslint-disable-next-line react/no-multi-comp
export const ProducerDetailEditPage = () => <ProducerDetailPageInner mode="edit" />;
